/**
 * Phonics Word Images
 * Emoji images for phonics example words
 */

export interface WordImage {
  word: string;
  emoji: string;
  category?: string;
}

/**
 * Word to emoji mapping
 * Using emojis for visual representation (free, no external resources needed)
 */
export const wordImages: Record<string, WordImage> = {
  // ===== Short Vowels - a =====
  'cat': { word: 'cat', emoji: '🐱', category: 'animal' },
  'hat': { word: 'hat', emoji: '🎩', category: 'clothing' },
  'map': { word: 'map', emoji: '🗺️', category: 'object' },
  'bag': { word: 'bag', emoji: '👜', category: 'object' },
  'dad': { word: 'dad', emoji: '👨', category: 'family' },

  // ===== Short Vowels - e =====
  'bed': { word: 'bed', emoji: '🛏️', category: 'furniture' },
  'red': { word: 'red', emoji: '🔴', category: 'color' },
  'pen': { word: 'pen', emoji: '🖊️', category: 'object' },
  'leg': { word: 'leg', emoji: '🦵', category: 'body' },
  'egg': { word: 'egg', emoji: '🥚', category: 'food' },

  // ===== Short Vowels - i =====
  'sit': { word: 'sit', emoji: '🪑', category: 'action' },
  'pig': { word: 'pig', emoji: '🐷', category: 'animal' },
  'big': { word: 'big', emoji: '📏', category: 'size' },
  'lip': { word: 'lip', emoji: '👄', category: 'body' },
  'win': { word: 'win', emoji: '🏆', category: 'action' },

  // ===== Short Vowels - o =====
  'hot': { word: 'hot', emoji: '🔥', category: 'temperature' },
  'dog': { word: 'dog', emoji: '🐕', category: 'animal' },
  'box': { word: 'box', emoji: '📦', category: 'object' },
  'fox': { word: 'fox', emoji: '🦊', category: 'animal' },
  'top': { word: 'top', emoji: '🔝', category: 'position' },

  // ===== Short Vowels - u =====
  'cup': { word: 'cup', emoji: '☕', category: 'object' },
  'bus': { word: 'bus', emoji: '🚌', category: 'transport' },
  'sun': { word: 'sun', emoji: '☀️', category: 'nature' },
  'nut': { word: 'nut', emoji: '🥜', category: 'food' },
  'cut': { word: 'cut', emoji: '✂️', category: 'action' },

  // ===== Long Vowels - a_e =====
  'cake': { word: 'cake', emoji: '🎂', category: 'food' },
  'make': { word: 'make', emoji: '🔨', category: 'action' },
  'name': { word: 'name', emoji: '🏷️', category: 'object' },
  'late': { word: 'late', emoji: '⏰', category: 'time' },
  'face': { word: 'face', emoji: '😊', category: 'body' },

  // ===== Long Vowels - i_e =====
  'like': { word: 'like', emoji: '👍', category: 'action' },
  'time': { word: 'time', emoji: '⏰', category: 'time' },
  'bike': { word: 'bike', emoji: '🚲', category: 'transport' },
  'nice': { word: 'nice', emoji: '😊', category: 'feeling' },
  'kite': { word: 'kite', emoji: '🪁', category: 'toy' },

  // ===== Long Vowels - o_e =====
  'home': { word: 'home', emoji: '🏠', category: 'place' },
  'bone': { word: 'bone', emoji: '🦴', category: 'object' },
  'rose': { word: 'rose', emoji: '🌹', category: 'plant' },
  'nose': { word: 'nose', emoji: '👃', category: 'body' },
  'hope': { word: 'hope', emoji: '🤞', category: 'feeling' },

  // ===== Long Vowels - u_e =====
  'tube': { word: 'tube', emoji: '🧪', category: 'object' },
  'cute': { word: 'cute', emoji: '🥰', category: 'feeling' },
  'rule': { word: 'rule', emoji: '📋', category: 'object' },
  'huge': { word: 'huge', emoji: '🐘', category: 'size' },

  // ===== Vowel Teams - ee =====
  'see': { word: 'see', emoji: '👀', category: 'action' },
  'tree': { word: 'tree', emoji: '🌳', category: 'plant' },
  'bee': { word: 'bee', emoji: '🐝', category: 'animal' },
  'green': { word: 'green', emoji: '🟢', category: 'color' },
  'sleep': { word: 'sleep', emoji: '😴', category: 'action' },

  // ===== Vowel Teams - ea =====
  'sea': { word: 'sea', emoji: '🌊', category: 'nature' },
  'tea': { word: 'tea', emoji: '🍵', category: 'food' },
  'eat': { word: 'eat', emoji: '🍽️', category: 'action' },
  'read': { word: 'read', emoji: '📖', category: 'action' },
  'team': { word: 'team', emoji: '👥', category: 'group' },

  // ===== Vowel Teams - ai/ay =====
  'rain': { word: 'rain', emoji: '🌧️', category: 'weather' },
  'train': { word: 'train', emoji: '🚂', category: 'transport' },
  'wait': { word: 'wait', emoji: '⏳', category: 'action' },
  'day': { word: 'day', emoji: '☀️', category: 'time' },
  'play': { word: 'play', emoji: '🎮', category: 'action' },

  // ===== Vowel Teams - oa =====
  'boat': { word: 'boat', emoji: '⛵', category: 'transport' },
  'coat': { word: 'coat', emoji: '🧥', category: 'clothing' },
  'road': { word: 'road', emoji: '🛣️', category: 'place' },
  'goat': { word: 'goat', emoji: '🐐', category: 'animal' },
  'soap': { word: 'soap', emoji: '🧼', category: 'object' },

  // ===== Vowel Teams - oo =====
  'moon': { word: 'moon', emoji: '🌙', category: 'nature' },
  'book': { word: 'book', emoji: '📚', category: 'object' },
  'food': { word: 'food', emoji: '🍔', category: 'food' },
  'good': { word: 'good', emoji: '👍', category: 'feeling' },
  'cool': { word: 'cool', emoji: '😎', category: 'feeling' },

  // ===== Vowel Teams - igh =====
  'high': { word: 'high', emoji: '🔝', category: 'position' },
  'night': { word: 'night', emoji: '🌙', category: 'time' },
  'light': { word: 'light', emoji: '💡', category: 'object' },
  'right': { word: 'right', emoji: '➡️', category: 'direction' },
  'sight': { word: 'sight', emoji: '👁️', category: 'sense' },

  // ===== Digraphs - sh =====
  'ship': { word: 'ship', emoji: '🚢', category: 'transport' },
  'shop': { word: 'shop', emoji: '🏪', category: 'place' },
  'fish': { word: 'fish', emoji: '🐟', category: 'animal' },
  'she': { word: 'she', emoji: '👧', category: 'person' },
  'shoe': { word: 'shoe', emoji: '👟', category: 'clothing' },

  // ===== Digraphs - ch =====
  'chair': { word: 'chair', emoji: '🪑', category: 'furniture' },
  'child': { word: 'child', emoji: '👶', category: 'person' },
  'chicken': { word: 'chicken', emoji: '🐔', category: 'animal' },
  'school': { word: 'school', emoji: '🏫', category: 'place' },
  'church': { word: 'church', emoji: '⛪', category: 'place' },

  // ===== Digraphs - th =====
  'think': { word: 'think', emoji: '🤔', category: 'action' },
  'three': { word: 'three', emoji: '3️⃣', category: 'number' },
  'this': { word: 'this', emoji: '👆', category: 'pronoun' },
  'that': { word: 'that', emoji: '👉', category: 'pronoun' },
  'mother': { word: 'mother', emoji: '👩', category: 'family' },

  // ===== Digraphs - ng =====
  'sing': { word: 'sing', emoji: '🎤', category: 'action' },
  'ring': { word: 'ring', emoji: '💍', category: 'object' },
  'king': { word: 'king', emoji: '👑', category: 'person' },
  'long': { word: 'long', emoji: '📏', category: 'size' },
  'song': { word: 'song', emoji: '🎵', category: 'music' },

  // ===== Digraphs - ck =====
  'back': { word: 'back', emoji: '🔙', category: 'position' },
  'duck': { word: 'duck', emoji: '🦆', category: 'animal' },
  'clock': { word: 'clock', emoji: '🕐', category: 'object' },
  'black': { word: 'black', emoji: '⬛', category: 'color' },
  'kick': { word: 'kick', emoji: '🦶', category: 'action' },

  // ===== Blends - l-blends =====
  'blue': { word: 'blue', emoji: '🔵', category: 'color' },
  'flower': { word: 'flower', emoji: '🌸', category: 'plant' },
  'glad': { word: 'glad', emoji: '😊', category: 'feeling' },

  // ===== Blends - r-blends =====
  'bread': { word: 'bread', emoji: '🍞', category: 'food' },
  'cry': { word: 'cry', emoji: '😢', category: 'action' },
  'drink': { word: 'drink', emoji: '🥤', category: 'action' },
  'frog': { word: 'frog', emoji: '🐸', category: 'animal' },

  // ===== Blends - s-blends =====
  'scan': { word: 'scan', emoji: '🔍', category: 'action' },
  'sky': { word: 'sky', emoji: '☁️', category: 'nature' },
  'small': { word: 'small', emoji: '🔻', category: 'size' },
  'snake': { word: 'snake', emoji: '🐍', category: 'animal' },
  'stop': { word: 'stop', emoji: '🛑', category: 'action' },

  // ===== Blends - ending =====
  'hand': { word: 'hand', emoji: '✋', category: 'body' },
  'ant': { word: 'ant', emoji: '🐜', category: 'animal' },
  'fast': { word: 'fast', emoji: '⚡', category: 'speed' },
  'cold': { word: 'cold', emoji: '❄️', category: 'temperature' },
  'bank': { word: 'bank', emoji: '🏦', category: 'place' },

  // ===== Special Rules - silent e =====
  // (covered in long vowels)

  // ===== Special Rules - silent k =====
  'know': { word: 'know', emoji: '🧠', category: 'action' },
  'knee': { word: 'knee', emoji: '🦵', category: 'body' },
  'knife': { word: 'knife', emoji: '🔪', category: 'object' },
  'knock': { word: 'knock', emoji: '🚪', category: 'action' },

  // ===== Special Rules - silent w =====
  'write': { word: 'write', emoji: '✍️', category: 'action' },
  'wrong': { word: 'wrong', emoji: '❌', category: 'judgment' },
  'wrap': { word: 'wrap', emoji: '🎁', category: 'action' },

  // ===== Special Rules - soft c =====
  'city': { word: 'city', emoji: '🏙️', category: 'place' },
  'cent': { word: 'cent', emoji: '¢', category: 'money' },

  // ===== Special Rules - soft g =====
  'gym': { word: 'gym', emoji: '🏋️', category: 'place' },
  'giant': { word: 'giant', emoji: '👤', category: 'size' },
  'go': { word: 'go', emoji: '➡️', category: 'action' },
  'game': { word: 'game', emoji: '🎯', category: 'activity' },

  // ===== Special Rules - r-controlled =====
  'car': { word: 'car', emoji: '🚗', category: 'transport' },
  'for': { word: 'for', emoji: '🎁', category: 'preposition' },
  'bird': { word: 'bird', emoji: '🐦', category: 'animal' },
  'her': { word: 'her', emoji: '👩', category: 'pronoun' },
  'turn': { word: 'turn', emoji: '🔄', category: 'action' },
};

/**
 * Get word image by word
 */
export function getWordImage(word: string): WordImage | undefined {
  return wordImages[word.toLowerCase()];
}

/**
 * Get word emoji by word
 */
export function getWordEmoji(word: string): string {
  return wordImages[word.toLowerCase()]?.emoji || '📖';
}

/**
 * Get all words for a category
 */
export function getWordsByCategory(category: string): WordImage[] {
  return Object.values(wordImages).filter(w => w.category === category);
}

/**
 * Get random words for game options
 */
export function getRandomWords(count: number, exclude: string[] = []): WordImage[] {
  const available = Object.values(wordImages).filter(w => !exclude.includes(w.word));
  const shuffled = available.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}
